import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LinkIcon,
  PlusIcon,
  ClipboardIcon,
  CheckIcon,
  LockClosedIcon,
  LockOpenIcon,
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  EyeIcon,
  CalendarIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../utils/supabaseClient';

// Helper to check for missing session metadata
const getMissingMetadata = (session) => {
  const missing = [];
  if (!session?.operator) missing.push('Operator');
  if (!session?.direction) missing.push('Direction');
  if (!session?.lane_no) missing.push('Lane Number');
  return missing;
};

// Metadata Warning Component
const MetadataWarning = ({ missingFields, onGoToDetails }) => {
  if (missingFields.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
      <div className="flex gap-2">
        <InformationCircleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-amber-800">
            <span className="font-medium">Missing session info:</span>{' '}
            {missingFields.join(', ')}
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Consider filling these out before sharing.{' '}
            <button
              type="button"
              onClick={onGoToDetails}
              className="text-amber-700 hover:text-amber-900 underline font-medium"
            >
              Go to Details tab
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

const ShareTab = ({ session, sessionId }) => {
  const [, setSearchParams] = useSearchParams();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  // Check for missing metadata
  const missingMetadata = useMemo(() => getMissingMetadata(session), [session]);

  const handleGoToDetails = useCallback(() => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSearchParams({ tab: 'details' });
  }, [setSearchParams]);

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('shared_links')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setLinks(data || []);
    } catch (err) {
      console.error('Error fetching share links:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchLinks();
    }
  }, [sessionId, fetchLinks]);

  const handleCopyLink = async (link) => {
    const url = `${window.location.origin}/share/${link.token}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleLinkCreated = (newLink) => {
    setLinks(prev => [newLink, ...prev]);
    setShowCreateModal(false);
  };

  const handleLinkUpdated = (updatedLink) => {
    setLinks(prev => prev.map(link => 
      link.id === updatedLink.id ? updatedLink : link
    ));
    setShowEditModal(false);
    setEditingLink(null);
  };

  const handleDisableLink = async () => {
    if (!editingLink) return;
    
    try {
      const { error: updateError } = await supabase
        .from('shared_links')
        .update({ is_active: false })
        .eq('id', editingLink.id);

      if (updateError) throw updateError;

      setLinks(prev => prev.map(link => 
        link.id === editingLink.id ? { ...link, is_active: false } : link
      ));
      setShowDisableModal(false);
      setEditingLink(null);
    } catch (err) {
      console.error('Error disabling link:', err);
      alert('Failed to disable link: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isExpired = (link) => {
    if (!link.expires_at) return false;
    return new Date(link.expires_at) < new Date();
  };

  // Only one active link allowed
  const activeLink = useMemo(() => 
    links.find(link => link.is_active && !isExpired(link)), 
    [links]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-sm border border-gray-200 shadow-sm">
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-red-700 text-sm">
            <p className="font-medium">Error loading share links</p>
            <p className="mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white flex-1 flex flex-col min-h-0 border-b border-gray-200">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Share Link</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {activeLink ? 'Manage your shareable link' : 'Create a shareable link for external reviewers'}
            </p>
          </div>
          {!activeLink && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-sm hover:bg-red-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Create Link
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-1 overflow-auto min-h-0">
        {activeLink ? (
          /* Active Link Card */
          <ActiveLinkCard
            link={activeLink}
            copiedId={copiedId}
            onCopy={handleCopyLink}
            onEdit={() => {
              setEditingLink(activeLink);
              setShowEditModal(true);
            }}
            onDisable={() => {
              setEditingLink(activeLink);
              setShowDisableModal(true);
            }}
            formatDate={formatDate}
          />
        ) : (
          /* Empty State */
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-full mb-4">
                <LinkIcon className="h-7 w-7 text-gray-400" />
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-1">No active link</h4>
              <p className="text-xs text-gray-500 mb-4 max-w-xs">
                Create a link to give external reviewers access to this session.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-sm hover:bg-red-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Create Link
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <ShareLinkModal
          session={session}
          sessionId={sessionId}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleLinkCreated}
          missingMetadata={missingMetadata}
          onGoToDetails={handleGoToDetails}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingLink && (
        <EditLinkModal
          link={editingLink}
          onClose={() => {
            setShowEditModal(false);
            setEditingLink(null);
          }}
          onUpdated={handleLinkUpdated}
          missingMetadata={missingMetadata}
          onGoToDetails={handleGoToDetails}
        />
      )}

      {/* Disable Confirmation Modal */}
      {showDisableModal && editingLink && (
        <DisableModal
          link={editingLink}
          onClose={() => {
            setShowDisableModal(false);
            setEditingLink(null);
          }}
          onConfirm={handleDisableLink}
        />
      )}
    </div>
  );
};

// Active Link Card Component
const ActiveLinkCard = ({ link, copiedId, onCopy, onEdit, onDisable, formatDate }) => {
  const shareUrl = `${window.location.origin}/share/${link.token}`;
  
  return (
    <div className="border border-gray-200 rounded-sm">
      {/* Link URL Bar */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-sm flex-1 min-w-0">
            <code className="text-sm text-gray-700 font-mono truncate">
              {shareUrl}
            </code>
          </div>
          <button
            onClick={() => onCopy(link)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
          >
            {copiedId === link.id ? (
              <>
                <CheckIcon className="h-4 w-4 mr-1.5 text-green-600" />
                Copied
              </>
            ) : (
              <>
                <ClipboardIcon className="h-4 w-4 mr-1.5" />
                Copy
              </>
            )}
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white border border-transparent hover:border-gray-300 rounded-sm transition-colors"
            title="Open link"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Link Details */}
      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Security */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Security</div>
            <div className="flex items-center gap-1.5 text-sm text-gray-900">
              {link.password_hash ? (
                <>
                  <LockClosedIcon className="h-4 w-4 text-amber-500" />
                  Password Protected
                </>
              ) : (
                <>
                  <LockOpenIcon className="h-4 w-4 text-gray-400" />
                  Open Access
                </>
              )}
            </div>
          </div>

          {/* Expiration */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Expires</div>
            <div className="flex items-center gap-1.5 text-sm text-gray-900">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              {link.expires_at ? formatDate(link.expires_at) : 'Never'}
            </div>
          </div>

          {/* Views */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Views</div>
            <div className="flex items-center gap-1.5 text-sm text-gray-900">
              <EyeIcon className="h-4 w-4 text-gray-400" />
              {link.view_count}
            </div>
          </div>

          {/* Created */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Created</div>
            <div className="text-sm text-gray-900">
              {formatDate(link.created_at)}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        {(link.contact_name || link.contact_email) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Contact</div>
            <div className="text-sm text-gray-900">
              {link.contact_name && <span>{link.contact_name}</span>}
              {link.contact_name && link.contact_email && <span className="text-gray-400"> · </span>}
              {link.contact_email && (
                <a href={`mailto:${link.contact_email}`} className="text-red-600 hover:underline">
                  {link.contact_email}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onEdit}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
          >
            <PencilIcon className="h-4 w-4 mr-1.5" />
            Edit
          </button>
          <button
            onClick={onDisable}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-sm hover:bg-red-50 transition-colors"
          >
            Disable Link
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Link Modal (simplified inline version)
const ShareLinkModal = ({ session, sessionId, onClose, onCreated, missingMetadata, onGoToDetails }) => {
  const [formData, setFormData] = useState({
    password: '',
    usePassword: false,
    expiresIn: '30', // days
    neverExpires: false,
    contactName: '',
    contactEmail: '',
  });
  const [creating, setCreating] = useState(false);

  // Generate random token
  const generateToken = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    return Array.from({ length: 16 }, () => 
      chars[Math.floor(Math.random() * chars.length)]
    ).join('');
  };

  // Hash password client-side
  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleCreate = async () => {
    try {
      setCreating(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const token = generateToken();
      const passwordHash = formData.usePassword && formData.password 
        ? await hashPassword(formData.password) 
        : null;
      const expiresAt = formData.neverExpires 
        ? null 
        : new Date(Date.now() + parseInt(formData.expiresIn) * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('shared_links')
        .insert({
          token,
          session_id: sessionId,
          created_by: user.id,
          password_hash: passwordHash,
          expires_at: expiresAt,
          contact_name: formData.contactName || null,
          contact_email: formData.contactEmail || null,
        })
        .select()
        .single();

      if (error) throw error;
      onCreated(data);
    } catch (err) {
      console.error('Error creating link:', err);
      alert('Failed to create link: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Create Share Link</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-4 space-y-4">
          {/* Metadata Warning */}
          <MetadataWarning 
            missingFields={missingMetadata} 
            onGoToDetails={onGoToDetails} 
          />

          {/* Password Protection */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.usePassword}
                onChange={(e) => setFormData({ ...formData, usePassword: e.target.checked })}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700">Password protect this link</span>
            </label>
            {formData.usePassword && (
              <input
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            )}
          </div>

          {/* Expiration */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.neverExpires}
                onChange={(e) => setFormData({ ...formData, neverExpires: e.target.checked })}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700">Never expires</span>
            </label>
            {!formData.neverExpires && (
              <select
                value={formData.expiresIn}
                onChange={(e) => setFormData({ ...formData, expiresIn: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="7">Expires in 7 days</option>
                <option value="14">Expires in 14 days</option>
                <option value="30">Expires in 30 days</option>
                <option value="60">Expires in 60 days</option>
                <option value="90">Expires in 90 days</option>
              </select>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Contact Info (Optional)</div>
            <input
              type="text"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              placeholder="Your name"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <input
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              placeholder="Your email"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || (formData.usePassword && !formData.password)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {creating ? 'Creating...' : 'Create Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Link Modal
const EditLinkModal = ({ link, onClose, onUpdated, missingMetadata, onGoToDetails }) => {
  const [formData, setFormData] = useState({
    password: '',
    usePassword: !!link.password_hash,
    changePassword: false,
    neverExpires: !link.expires_at,
    customExpiry: link.expires_at ? new Date(link.expires_at).toISOString().split('T')[0] : '',
    contactName: link.contact_name || '',
    contactEmail: link.contact_email || '',
  });
  const [saving, setSaving] = useState(false);

  // Hash password client-side (same as ShareLinkModal)
  const hashPassword = async (password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updates = {
        contact_name: formData.contactName || null,
        contact_email: formData.contactEmail || null,
        expires_at: formData.neverExpires 
          ? null 
          : formData.customExpiry 
            ? new Date(formData.customExpiry).toISOString()
            : null,
      };

      // Handle password changes
      if (formData.changePassword) {
        if (formData.usePassword && formData.password) {
          updates.password_hash = await hashPassword(formData.password);
        } else if (!formData.usePassword) {
          updates.password_hash = null;
        }
      }

      const { data, error } = await supabase
        .from('shared_links')
        .update(updates)
        .eq('id', link.id)
        .select()
        .single();

      if (error) throw error;
      onUpdated(data);
    } catch (err) {
      console.error('Error updating link:', err);
      alert('Failed to update link: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Edit Share Link</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-4 space-y-4">
          {/* Metadata Warning */}
          <MetadataWarning 
            missingFields={missingMetadata} 
            onGoToDetails={onGoToDetails} 
          />

          {/* Password Protection */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.usePassword}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  usePassword: e.target.checked,
                  changePassword: true 
                })}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700">Password protect this link</span>
            </label>
            {formData.usePassword && (
              <div className="mt-2 space-y-2">
                {link.password_hash && !formData.changePassword ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Current password is set</span>
                    <button
                      onClick={() => setFormData({ ...formData, changePassword: true })}
                      className="text-red-600 hover:underline"
                    >
                      Change password
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value, changePassword: true })}
                    placeholder="Enter new password"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                )}
              </div>
            )}
          </div>

          {/* Expiration */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Expiration</div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.neverExpires}
                onChange={(e) => setFormData({ ...formData, neverExpires: e.target.checked })}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Never expires</span>
            </label>
            {!formData.neverExpires && (
              <input
                type="date"
                value={formData.customExpiry}
                onChange={(e) => setFormData({ ...formData, customExpiry: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Contact Info</div>
            <input
              type="text"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              placeholder="Your name"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <input
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              placeholder="Your email"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (formData.usePassword && formData.changePassword && !formData.password)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Disable Confirmation Modal
const DisableModal = ({ link, onClose, onConfirm }) => {
  const [disabling, setDisabling] = useState(false);

  const handleConfirm = async () => {
    setDisabling(true);
    await onConfirm();
    setDisabling(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-sm mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Disable Link</h3>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to disable this share link? Anyone with the link will no longer be able to access this session.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            This action cannot be undone. You will need to create a new link to share this session again.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={disabling}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {disabling ? 'Disabling...' : 'Disable Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareTab;